/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description Patients Creation View Page.
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppCardLayout from '@nephro/components/AppCardLayout';
import { Input, Form, Row, Col, Select } from 'antd';

// Components
import FormGenerator from '@nephro/components/FormGenerator';

// Helpers
import {
  bottomOptionsCreateEdit,
  onGetItemById
} from '@nephro/commons/helpers/utils';
import { onKeepOnlyNumbers } from '@nephro/commons/helpers/string-helper';

// Styles
import styles from './styles.module.css';

const patientData = {};
const { Option } = Select;
/**
 * Patients Creation View
 * @param { Object } patientActions - Patient Actions.
 * @param { Object } actions - App Actions.
 * @return { Node } React node.
 */
const PatientsCreationView = ({ patientActions }) => {
  const [identificationTypesList, setIdentificationTypesList] =
    React.useState(null);
  const history = useHistory();
  const { createPatient, getIdentificationTypesArray } = patientActions;
  const { t: translate } = useTranslation();

  const onGoBack = () => {
    history.goBack();
  };

  // Function to get on submit form values
  const onFinish = (values) => {
    const identification_type = onGetItemById(
      identificationTypesList?.content,
      'identification_type_id',
      values.docType
    );
    const data = {
      ...values,
      patient_status: {
        id_patient_status: 2,
        description: 'inactivo'
      },
      patient_class: {
        description: 'normal',
        id_patient_class: 1
      },
      identification_type
    };
    createPatient(data, onGoBack);
  };

  // Form item list
  const formItemList = [
    {
      item: <Input />,
      name: ['first_name'],
      label: translate('patientMg.name'),
      rules: [{ required: true }],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input />,
      name: ['last_name'],
      label: translate('patientMg.lastName'),
      rules: [{ required: true }],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input />,
      name: ['email'],
      label: translate('patientMg.email'),
      rules: [{ required: true, type: 'email' }],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input />,
      name: ['contact_phone_number'],
      label: translate('patientMg.phoneNumber'),
      rules: [{ required: true }],
      normalize: onKeepOnlyNumbers,
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: (
        <Row gutter={10} className={styles.mBottom}>
          <Col span={10}>
            <Form.Item
              rules={[{ required: true, message: 'El tipo es requerido' }]}
              className={styles.mBottom}
              name={['docType']}>
              <Select
                showSearch
                placeholder={translate('patientMg.type')}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }>
                {identificationTypesList?.content?.map((item, index) => (
                  <Option key={index} value={item.identification_type_id}>
                    {item.code}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={14}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'El número es requerido'
                }
              ]}
              normalize={onKeepOnlyNumbers}
              className={styles.mBottom}
              name={['id_document']}>
              <Input placeholder={translate('patientMg.number')} />
            </Form.Item>
          </Col>
        </Row>
      ),
      label: translate('patientMg.document'),
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    }
  ];

  // Function to get and set identification types list
  const onGetIdentificationTypesList = (_, response) => {
    if (response?.data?.content) {
      setIdentificationTypesList(response.data);
    } else {
      onGoBack();
    }
  };

  React.useEffect(() => {
    getIdentificationTypesArray({ paged: false }, onGetIdentificationTypesList);
  }, []);

  return (
    <AppCardLayout>
      <div className="flex flex-col h-full px-7 pt-5 pb-7 ">
        <FormGenerator
          initialValues={patientData}
          onFinish={onFinish}
          items={formItemList}
          bottomOptions={bottomOptionsCreateEdit(
            onGoBack,
            translate,
            'create',
            styles.actionButton
          )}
        />
      </div>
    </AppCardLayout>
  );
};

/**
 * Component definition of proptypes.
 */
PatientsCreationView.propTypes = {
  patientActions: PropTypes.shape({
    createPatient: PropTypes.func.isRequired,
    getIdentificationTypesArray: PropTypes.func.isRequired
  }).isRequired
};

export default PatientsCreationView;
